import { faker } from '@faker-js/faker/locale/es';
import type { Defect, DefectSeverity, DefectType, Person, Station } from './types';
import type { WorkOrderRow } from './workOrderGenerator';

const SEVERITY_MIX: Array<{ value: DefectSeverity; weight: number }> = [
  { value: 'CRITICAL', weight: 0.10 },
  { value: 'MAJOR', weight: 0.30 },
  { value: 'MINOR', weight: 0.60 },
];

const TYPES: DefectType[] = ['scratch', 'dent', 'porosity', 'alignment', 'burn', 'tolerance'];

const TYPE_DESCRIPTIONS: Record<DefectType, string[]> = {
  scratch: ['Scratch lateral panel', 'Arañazo en superficie', 'Marca abrasiva visible'],
  dent: ['Golpe en chapa', 'Abolladura esquina', 'Deformación menor'],
  porosity: ['Porosidad cordón soldadura', 'Burbuja en revestimiento', 'Microcráter visible'],
  alignment: ['Desalineación rail', 'Holgura fuera tolerancia', 'Posicionamiento incorrecto'],
  burn: ['Marca quemado pintura', 'Sobrecalentamiento sensor', 'Quemadura láser'],
  tolerance: ['Tolerancia fuera rango', 'Cota +0.3mm vs spec', 'Diámetro fuera límite'],
};

export function generateDefects(
  workOrders: WorkOrderRow[],
  stations: Station[],
  people: Person[],
  count: number = 60,
): Defect[] {
  const defects: Defect[] = [];
  const inspectors = people.filter((p) => p.role === 'quality_engineer' || p.role === 'operator');
  const eligibleWOs = workOrders.filter((w) => w.status !== 'QUEUED' && w.status !== 'CANCELLED');
  const year = new Date().getFullYear();

  for (let i = 0; i < count; i++) {
    const wo = faker.helpers.arrayElement(eligibleWOs);
    const station = stations.find((s) => s.id === wo.station) ?? faker.helpers.arrayElement(stations);
    const inspector = faker.helpers.arrayElement(inspectors);
    const severity = faker.helpers.weightedArrayElement(SEVERITY_MIX);
    const type = faker.helpers.arrayElement(TYPES);
    const detectedAt = faker.date.recent({ days: 7 });

    let disposition: Defect['disposition'];
    if (severity === 'CRITICAL') {
      disposition = faker.helpers.weightedArrayElement([
        { value: 'PENDING', weight: 0.50 },
        { value: 'SCRAP', weight: 0.40 },
        { value: 'REWORK', weight: 0.10 },
      ]);
    } else if (severity === 'MAJOR') {
      disposition = faker.helpers.weightedArrayElement([
        { value: 'PENDING', weight: 0.30 },
        { value: 'REWORK', weight: 0.50 },
        { value: 'SCRAP', weight: 0.10 },
        { value: 'RELEASE', weight: 0.10 },
      ]);
    } else {
      disposition = faker.helpers.weightedArrayElement([
        { value: 'RELEASE', weight: 0.55 },
        { value: 'REWORK', weight: 0.25 },
        { value: 'PENDING', weight: 0.20 },
      ]);
    }

    defects.push({
      id: `NC-${year}-${String(i + 1).padStart(3, '0')}`,
      woId: wo.id,
      serial: `SN-${faker.string.numeric(5)}`,
      severity,
      type,
      disposition,
      station: station.id,
      detectedBy: inspector.id,
      detectedAt: detectedAt.toISOString(),
      description: faker.helpers.arrayElement(TYPE_DESCRIPTIONS[type]),
    });
  }

  return defects.sort((a, b) => +new Date(b.detectedAt) - +new Date(a.detectedAt));
}
